import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { useAppContext } from "../../../contexts/AppContext";
import { useFieldConfig } from "../../../contexts/FieldConfigContext";
import { useFeatureAccess } from "../../../contexts/LicenseContext";
import { useBasePath } from "../../../contexts/BasePathContext";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@exiftrator/shared/components/ui";
import { cn } from "../../utils/cn";

const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

const ALL_TIMEZONES = Intl.supportedValuesOf("timeZone");

function GeneralSettings() {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const fieldAccess = useFeatureAccess("fieldCustomization");
  const { timezone, setTimezone } = useAppContext();
  const [open, setOpen] = React.useState(false);
  const { exportConfig, importConfig, resetToDefaults } = useFieldConfig();
  const [importError, setImportError] = useState(null);

  // Handle export
  const handleExport = () => {
    const configJson = exportConfig();
    const blob = new Blob([configJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `exiftrator-config-${new Date().toISOString().split("T")[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle import
  const handleImport = (event) => {
    if (!fieldAccess.allowed) {
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = importConfig(e.target.result);
        if (result.success) {
          setImportError(null);
          alert("Configuration imported successfully!");
        } else {
          setImportError(result.errors.join(", "));
        }
      } catch (error) {
        setImportError(error.message);
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be imported again
    event.target.value = "";
  };

  const handleReset = () => {
    if (!fieldAccess.allowed) {
      return;
    }

    if (
      confirm(
        "Reset to default configuration? This will enable only the 7 basic fields and restore default category order.",
      )
    ) {
      resetToDefaults();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-sans text-[24px] font-bold text-text-primary">
          General Settings
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-white/10 pb-8 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-[14px] font-bold text-text-primary">
              Timezone
            </h3>
            <p className="text-[12px] text-text-tertiary">
              Select the timezone to use for displaying dates and times in the
              calendar view.
            </p>
          </div>
          <div className="max-w-md">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                >
                  {timezone
                    ? timezone.replace(/_/g, " ")
                    : "Select timezone..."}
                  <ChevronsUpDown className="opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
              <Command>
                <CommandInput placeholder="Search timezones..." />
                <CommandList>
                  <CommandEmpty>No timezone found.</CommandEmpty>
                  <CommandGroup heading="Common">
                    {COMMON_TIMEZONES.map((tz) => (
                      <CommandItem
                        key={tz}
                        value={tz}
                        onSelect={() => {
                          setTimezone(tz);
                          setOpen(false);
                        }}
                      >
                        {tz.replace(/_/g, " ")}
                        <Check
                          className={cn(
                            "ml-auto",
                            timezone === tz ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandGroup heading="All Timezones">
                    {ALL_TIMEZONES.map((tz) => (
                      <CommandItem
                        key={tz}
                        value={tz}
                        onSelect={() => {
                          setTimezone(tz);
                          setOpen(false);
                        }}
                      >
                        {tz.replace(/_/g, " ")}
                        <Check
                          className={cn(
                            "ml-auto",
                            timezone === tz ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Backup & Restore */}
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-[14px] font-bold text-text-primary">
              Backup & Restore
            </h3>
            <p className="text-[12px] text-text-tertiary">
              Export or import your field configuration and preferences.
            </p>
          </div>

          {!fieldAccess.allowed && (
            <Alert className="border-accent-primary/30 bg-accent-primary/10">
              <AlertTitle>Import and reset require Pro</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Field list customization is currently locked on the Free tier.</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(appPath("/settings/license"))}
                >
                  Unlock Pro
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-4">
            <Button variant="default" size="lg" onClick={handleExport}>
              Download JSON
            </Button>
            <input
              type="file"
              id="import-file"
              accept=".json"
              onChange={handleImport}
              disabled={!fieldAccess.allowed}
              className="hidden"
            />
            <Button
              variant="secondary"
              size="lg"
              disabled={!fieldAccess.allowed}
              onClick={() => document.getElementById("import-file").click()}
            >
              Import File
            </Button>
          </div>
          {importError && (
            <div className="p-4 bg-accent-red/10 border border-accent-red/30 rounded text-accent-red text-[11px] font-sans leading-relaxed">
              Error importing configuration: {importError}
            </div>
          )}
        </div>

        {/* Factory Reset */}
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-[14px] font-bold text-accent-red">
              Factory Reset
            </h3>
            <p className="text-[12px] text-text-tertiary">
              Reset all settings and configuration to their original defaults.
              This action cannot be undone.
            </p>
          </div>
          <Button
            variant="destructive"
            size="lg"
            disabled={!fieldAccess.allowed}
            onClick={handleReset}
          >
            Reset All Settings
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GeneralSettings;
